import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardMedia from '@material-ui/core/CardMedia';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';
import EventIcon from '@material-ui/icons/Event';
import VisibilityIcon from '@material-ui/icons/Visibility';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import AddIcon from '@material-ui/icons/Add';

import { buildWatchUrl, formatDuration, formatPublishedDate, formatViews, getChannelLabel } from '../utils';

const PLACEHOLDER = 'data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="180"%3E%3Crect width="100%25" height="100%25" fill="%23e0e0e0"/%3E%3C/svg%3E';

const styles = theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  mediaWrapper: {
    position: 'relative',
    paddingTop: '56.25%', // 16/9
    backgroundColor: theme.palette.grey[200],
  },
  media: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  durationChip: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    height: 22,
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    fontWeight: 600,
  },
  privateChip: {
    position: 'absolute',
    top: 8,
    left: 8,
    height: 22,
  },
  content: {
    flex: 1,
    paddingBottom: theme.spacing(1),
  },
  title: {
    fontWeight: 600,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    minHeight: '2.6em',
    lineHeight: '1.3em',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  dateRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginTop: theme.spacing(1),
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  dateText: {
    flexShrink: 0,
    marginRight: theme.spacing(1),
  },
  // Channel tag shares the date line (no extra row): pushed right, it takes the
  // remaining space and only shrinks (label ellipsis + title tooltip) when the
  // card is too narrow for the full channel name.
  channelChip: {
    marginLeft: 'auto',
    height: 20,
    minWidth: 0,
    flexShrink: 1,
    fontSize: 11,
    fontWeight: 500,
  },
  channelChipLabel: {
    paddingLeft: 5,
    paddingRight: 5,
  },
  metaIcon: {
    fontSize: 16,
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  actions: {
    justifyContent: 'space-between',
    paddingTop: 0,
  },
  fill: {
    flex: 1,
  },
});

export class VideoCard extends Component {
  handleImgError = e => {
    if (e && e.target && e.target.src !== PLACEHOLDER) {
      e.target.src = PLACEHOLDER;
    }
  };

  handleCopy = () => {
    const { video, onCopyEmbed } = this.props;
    onCopyEmbed(video.id);
  };

  handleSelect = () => {
    const { video, onSelect } = this.props;
    onSelect(video.id);
  };

  renderMedia() {
    const { video, classes, messages: m } = this.props;
    const duration = formatDuration(video.duration);
    return (
      <div className={classes.mediaWrapper}>
        <CardMedia
          component="img"
          className={classes.media}
          image={video.thumbnail_360_url || PLACEHOLDER}
          title={video.title}
          onError={this.handleImgError}
        />
        {duration && <Chip size="small" label={duration} className={classes.durationChip} />}
        {video.private && (
          <Chip size="small" color="secondary" label={m.videos_private} className={classes.privateChip} />
        )}
      </div>
    );
  }

  renderContent() {
    const { video, classes, messages: m } = this.props;
    const channelLabel = getChannelLabel(video);
    const publishedDate = formatPublishedDate(video.created_time);
    return (
      <CardContent className={classes.content}>
        <Typography variant="subtitle2" className={classes.title} title={video.title}>
          {video.title}
        </Typography>
        <div className={classes.dateRow} data-test="VideoCard__date">
          <EventIcon className={classes.metaIcon} />
          {/* Date only (calendar icon + tooltip say "published on") so the channel tag fits on this line. */}
          <Typography
            variant="body2"
            color="inherit"
            noWrap
            className={classes.dateText}
            title={`${m.videos_publishedOn} ${publishedDate}`}
          >
            {publishedDate}
          </Typography>
          {channelLabel && (
            <Chip
              size="small"
              variant="outlined"
              label={channelLabel}
              title={channelLabel}
              className={classes.channelChip}
              classes={{ label: classes.channelChipLabel }}
              data-test="VideoCard__channel"
            />
          )}
        </div>
        <div className={classes.metaRow}>
          <VisibilityIcon className={classes.metaIcon} />
          <Typography variant="caption" color="textSecondary">
            {formatViews(video.views_total)} {m.videos_views}
          </Typography>
        </div>
      </CardContent>
    );
  }

  render() {
    const { video, classes, messages: m, onSelect } = this.props;
    const selectable = typeof onSelect === 'function';
    const body = (
      <React.Fragment>
        {this.renderMedia()}
        {this.renderContent()}
      </React.Fragment>
    );

    return (
      <Card className={classes.card} data-test="VideoCard">
        {selectable ? (
          <CardActionArea onClick={this.handleSelect} data-test="VideoCard__insert">
            {body}
          </CardActionArea>
        ) : (
          body
        )}

        <CardActions className={classes.actions}>
          <Tooltip title={m.videos_openOnDailymotion}>
            <IconButton
              data-test="VideoCard__openExternal"
              component="a"
              href={buildWatchUrl(video.id)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={m.videos_openOnDailymotion}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <div className={classes.fill} />
          {selectable ? (
            <Tooltip title={m.videos_insertEmbed}>
              <IconButton
                data-test="VideoCard__insertBtn"
                color="primary"
                onClick={this.handleSelect}
                aria-label={m.videos_insertEmbed}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={m.videos_copyEmbed}>
              <IconButton
                data-test="VideoCard__copyEmbed"
                color="primary"
                onClick={this.handleCopy}
                aria-label={m.videos_copyEmbed}
              >
                <FileCopyIcon />
              </IconButton>
            </Tooltip>
          )}
        </CardActions>
      </Card>
    );
  }
}

export default withStyles(styles)(VideoCard);
